import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { BiCheckCircle, BiCoinStack } from "react-icons/bi";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import "../assets/css/Monthlyprice.css";

const PLAN_CONTENT = {
    Starter: {
        title: "Starter Plan",
        description: "Best for beginners and casual users",
        heading: "Included:",
        features: [
            "Access to essential design elements",
            "PNG file access",
            "Unlimited downloads with coin-based access",
            "Credit top-ups available",
        ],
    },
    Premium: {
        title: "Premium Plan",
        description: "Best for creators who need more flexibility",
        heading: "Everything in Starter, plus:",
        features: [
            "Access to advanced design elements",
            "PNG file access",
            "Premium asset library access",
            "Credit top-ups available",
        ],
    },
    "Premium+": {
        title: "Premium + Plan",
        description: "Best for professionals and small teams",
        heading: "Everything in Premium, plus:",
        headerLabel: "Best value",
        featured: true,
        features: [
            "PSD file access",
            "Access to premium editable assets",
            "Unlimited downloads with coin-based access",
            "Early access to limited-edition drops",
        ],
        addOn: "Up to 10% discount on advanced items",
    },
    Pro: {
        title: "PRO Plan",
        description: "Best for power users, teams, and businesses",
        heading: "Everything in Premium+, plus:",
        headerLabel: "Most Popular",
        popular: true,
        features: [
            "PSD file access",
            "Priority access to all premium elements",
            "Unlimited downloads with coin-based access",
            "Early access to limited-edition drops",
        ],
        addOn: "Up to 15% discount on advanced items",
    },
};

const PLAN_ORDER = ["Starter", "Premium", "Premium+", "Pro"];

const formatPrice = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "--";
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(amount);
};

const Annualprice = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [waitlistEligible, setWaitlistEligible] = useState(false);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            setError("");
            const response = await api.get("/SubscriptionRoutes/subscriptions", {
                params: { plan_type: "Yearly" },
            });
            setPlans(Array.isArray(response?.data?.data) ? response.data.data : []);
            setWaitlistEligible(Boolean(response?.data?.waitlist_eligible));
        } catch (requestError) {
            console.error("Failed to fetch yearly subscriptions", requestError);
            setError(
                requestError?.response?.data?.message ||
                "Unable to load subscription plans. Please try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let mounted = true;

        const loadPlans = async () => {
            try {
                setLoading(true);
                setError("");
                const response = await api.get("/SubscriptionRoutes/subscriptions", {
                    params: { plan_type: "Yearly" },
                });

                if (mounted) {
                    setPlans(Array.isArray(response?.data?.data) ? response.data.data : []);
                    setWaitlistEligible(Boolean(response?.data?.waitlist_eligible));
                }
            } catch (requestError) {
                console.error("Failed to fetch yearly subscriptions", requestError);
                if (mounted) {
                    setError(
                        requestError?.response?.data?.message ||
                        "Unable to load subscription plans. Please try again.",
                    );
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };

        loadPlans();
        return () => { mounted = false; };
    }, []);

    const orderedPlans = useMemo(
        () =>
            PLAN_ORDER.map((name) => ({
                name,
                plan: plans.find(
                    (item) =>
                        String(item?.sub_name || "").trim().toLowerCase() === name.toLowerCase(),
                ),
            })),
        [plans],
    );

    if (loading) {
        return (
            <div className="pricing-loader" role="status" aria-live="polite">
                <div className="pricing-loader__spinner" aria-hidden="true" />
                <h5>Loading annual subscription plans</h5>
                <p>Please wait while we prepare the latest pricing.</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="pricing-error" role="alert">
                <h5>Plans could not be loaded</h5>
                <p>{error}</p>
                <Button
                    label="Try again"
                    icon="pi pi-refresh"
                    className="pricing-subscribe-button"
                    onClick={fetchPlans}
                />
            </div>
        );
    }

    return (
        <>
            {waitlistEligible && (
                <Row className="mx-0 mb-4">
                    <Col xs={12} className="px-0">
                        <div
                            className="alert alert-warning mb-0 d-flex align-items-start gap-3"
                            role="status"
                            style={{
                                borderRadius: "14px",
                                border: "1px solid rgba(255, 193, 7, 0.35)",
                            }}
                        >
                            <BiCoinStack
                                size={26}
                                className="flex-shrink-0 mt-1"
                                aria-hidden="true"
                            />

                            <div>
                                <strong className="d-block mb-1">
                                    Exclusive Waitlist Member Offers
                                </strong>

                                <span>
                                    You are eligible for special subscription pricing and
                                    one-time bonus credits. Your exclusive offers are shown
                                    below while you are logged in.
                                </span>
                            </div>
                        </div>
                    </Col>
                </Row>
            )}
            {!isAuthenticated && (
                <Row className="mx-0 mb-4">
                    <Col xs={12} className="px-0">
                        <div
                            className="alert alert-warning mb-0 d-flex align-items-start gap-3"
                            role="status"
                            style={{
                                borderRadius: "14px",
                                border: "1px solid rgba(255, 193, 7, 0.35)",
                            }}
                        >
                            <BiCoinStack
                                size={26}
                                className="flex-shrink-0 mt-1"
                                aria-hidden="true"
                            />

                            <div>
                                <strong className="d-block mb-1">
                                    Exclusive Waitlist Member Offers
                                </strong>

                                <span>
                                    Login To Claim Exclusive Offers if you are a waitlist member. If you are eligible for special subscription pricing and one-time bonus credits. Your exclusive offers will be shown below while you are logged in.
                                </span>
                            </div>
                        </div>
                    </Col>
                </Row>
            )}

            <Row className="justify-content-center g-4 pricing-grid">
                {orderedPlans.map(({ name, plan }) => {
                    const content = PLAN_CONTENT[name];
                    const isExclusive = Boolean(plan?.is_waitlist_offer);
                    const isPopular = Boolean(content.popular);
                    const isFeatured = Boolean(content.featured);

                    return (
                        <Col key={name} xs={12} md={6} lg={3} className="pricing-card">
                            <Card
                                className={[
                                    "pricing-plan-card ",
                                    isFeatured ? "pricing-plan-card--featured" : "",
                                    isPopular ? "pricing-plan-card--popular greegshadow" : "",
                                ].filter(Boolean).join(" ")}
                            >
                                <Card.Header
                                    className={[
                                        "pricing-plan-card__header",
                                        isFeatured ? "pricing-plan-card__header--featured" : "",
                                        isPopular ? "pricing-plan-card__header--popular greenbg" : "",
                                    ].filter(Boolean).join(" ")}
                                >
                                    {content.headerLabel || <span aria-hidden="true">&nbsp;</span>}
                                </Card.Header>

                                <Card.Body className="pricing-plan-card__body">
                                    <div className="pricing-plan-card__intro">
                                        <h4>{content.title}</h4>
                                        <p>{content.description}</p>
                                    </div>

                                    {isExclusive ? (
                                        <div
                                            className="offer-flip"
                                            aria-label="Exclusive bonus and exclusive price"
                                        >
                                            <div className="offer-flip__inner">
                                                <div className="offer-flip__face">





                                                    <div className="pricing-price-box__offer-row">
                                                        <span className="pricing-price-box__offer-badge">
                                                            Exclusive price
                                                        </span>

                                                        {Number(plan?.waitlist_discount_percentage) > 0 && (
                                                            <span className="pricing-price-box__discount">
                                                                {plan.waitlist_discount_percentage}% off
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="pricing-price-box__main">
                                                        <span className="pricing-price-box__currency">₹</span>
                                                        <span className="pricing-price-box__current">
                                                            {formatPrice(plan?.display_price)}
                                                        </span>
                                                        <span className="pricing-price-box__suffix">/-</span>
                                                    </div>

                                                    <div className="pricing-price-box__regular-row">
                                                        <span>Regular price</span>
                                                        <del>₹{formatPrice(plan?.strikethrough_price)}</del>
                                                    </div>

                                                    <div className="pricing-price-box__meta">
                                                        <span>{plan?.duration_days ?? "--"} days</span>
                                                        <span className="pricing-price-box__divider">•</span>
                                                        <span>Billed annually</span>
                                                    </div>



                                                </div>

                                                <div className="offer-flip__face offer-flip__face--back">
                                                    <div className="offer-flip__label">
                                                        Exclusive waitlist offer
                                                    </div>

                                                    <div className="offer-flip__bonus">
                                                        <BiCoinStack aria-hidden="true" />
                                                        <span>
                                                            Get <strong>{Number(plan?.waitlist_bonus_credits || 0).toLocaleString("en-IN")} extra coins</strong> on your first purchase
                                                        </span>
                                                    </div>

                                                    <div className="offer-flip__note">
                                                        One-time bonus only. Renewals receive the normal yearly plan credits.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="pricing-price-box">
                                            {plan?.strikethrough_price &&
                                                Number(plan.strikethrough_price) >
                                                Number(plan?.display_price ?? plan?.price) && (
                                                    <div className="pricing-price-box__strikethrough">
                                                        <del>
                                                            ₹{formatPrice(plan.strikethrough_price)}/-
                                                        </del>
                                                    </div>
                                                )}

                                            <div className="pricing-price-box__main">
                                                <span className="pricing-price-box__currency">₹</span>

                                                <span className="pricing-price-box__current">
                                                    {formatPrice(plan?.display_price ?? plan?.price)}
                                                </span>

                                                <span className="pricing-price-box__suffix">/-</span>
                                            </div>

                                            <div className="pricing-price-box__meta">
                                                <span>{plan?.duration_days ?? "--"} days</span>
                                                <span className="pricing-price-box__divider">•</span>
                                                <span>Billed annually</span>
                                            </div>
                                        </div>
                                    )}

                                    <Button
                                        label={isExclusive ? "Claim exclusive offer" : "Subscribe now"}
                                        className={[
                                            "w-100",
                                            "py-2",
                                            "my-3",
                                            "pricing-subscribe-button",
                                            isPopular ? "pricing-subscribe-button--popular" : "",
                                        ].filter(Boolean).join(" ")}
                                        disabled={!plan}
                                        onClick={() =>
                                            navigate("/checkout", {
                                                state: {
                                                    subscriptionId: plan.subscription_id,
                                                    isWaitlistOffer: isExclusive,
                                                },
                                            })
                                        }
                                    />

                                    <div className="pricing-features">
                                        <p className="pricing-features__heading">
                                            <strong>{content.heading}</strong>
                                        </p>

                                        <ul>
                                            <li>
                                                <BiCheckCircle />
                                                <span>{plan?.credits_per_month ?? "--"} credits/year</span>
                                            </li>

                                            {isExclusive && (
                                                <li className="pricing-features__bonus-item">
                                                    <BiCoinStack />
                                                    <span>{Number(plan?.waitlist_bonus_credits || 0).toLocaleString("en-IN")} bonus coins on the first purchase</span>
                                                </li>
                                            )}

                                            {!isExclusive && plan?.is_subscribe_offer && (
                                                <li className="pricing-features__bonus-item">
                                                    <BiCoinStack />
                                                    <span>Limited offer: {Number(plan.offer_bonus_credits).toLocaleString("en-IN")} bonus coins when you subscribe</span>
                                                </li>
                                            )}

                                            {content.features.map((feature) => (
                                                <li key={feature}>
                                                    <BiCheckCircle />
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        {content.addOn && (
                                            <p className="pricing-add-on">
                                                <strong>Add-ons:</strong>
                                                <span>{content.addOn}</span>
                                            </p>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>


            {/* <Row className="mx-0 pt-4">
                <Col xs={12} className="px-0">
                    <div className="annual-cancellation-note">
                        <strong>Cancel risk-free in the first 15 days</strong>
                        <span>
                            Cancel within the first 15 days for a refund if you have used less than 50%
                            of your plan credits. Otherwise, no refund will be issued and your subscription
                            will remain active until the end of the current billing period.
                        </span>
                    </div>
                </Col>
            </Row> */}

        </>
    );
};

export default Annualprice;